import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { gamesApi, venuesApi, usersApi, promotionsApi, leaguesApi } from '../services/api';
import { exportCSV } from '../utils/export';

function StatTile({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

function Games({ user, onLogout }) {
  const location = useLocation();
  const [games, setGames] = useState([]);
  const [venues, setVenues] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDealerModal, setShowDealerModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [formData, setFormData] = useState({
    gameType: 'Texas Holdem',
    venueId: '',
    startDate: '',
    startTime: '',
    maxPlaces: 10,
    isBigMoney: false,
  });
  const [saving, setSaving] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [allLeagues, setAllLeagues] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [selectedGameIds, setSelectedGameIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [preSelectedVenue, setPreSelectedVenue] = useState(null);

  // Sorting + filters + pagination + grouping
  const [sortKey, setSortKey] = useState(() => localStorage.getItem('gamesSortKey') || 'startTime');
  const [sortDir, setSortDir] = useState(() => localStorage.getItem('gamesSortDir') || 'desc');
  const [search, setSearch] = useState('');
  const [filterLeague, setFilterLeague] = useState('');
  const [filterVenue, setFilterVenue] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDealer, setFilterDealer] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [showFilters, setShowFilters] = useState(false);

  const handleSort = (key) => {
    if (sortKey === key) {
      const newDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(newDir);
      localStorage.setItem('gamesSortDir', newDir);
    } else {
      setSortKey(key);
      setSortDir('asc');
      localStorage.setItem('gamesSortKey', key);
      localStorage.setItem('gamesSortDir', 'asc');
    }
  };

  const sortIcon = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const handleDuplicate = async (game) => {
    const nextWeek = new Date(game.startTime || game.start_time);
    nextWeek.setDate(nextWeek.getDate() + 7);
    try {
      await gamesApi.create({
        gameType: game.gameType || game.game_type,
        venueId: game.venue?.id || game.venueId || game.venue_id,
        startTime: nextWeek.toISOString(),
        maxPlaces: game.maxPlaces || game.max_places || 10,
        leagueId: game.leagueId || game.league_id,
      });
      fetchData();
    } catch (e) {
      alert('Failed to duplicate: ' + e.message);
    }
  };

  const formatRelative = (dateStr) => {
    const d = new Date(dateStr);
    const diff = d - new Date();
    const absDiff = Math.abs(diff);
    const min = Math.floor(absDiff / 60000);
    const hour = Math.floor(min / 60);
    const day = Math.floor(hour / 24);
    if (diff > 0) {
      if (min < 60) return `in ${min}m`;
      if (hour < 24) return `in ${hour}h`;
      if (day < 7) return `in ${day}d`;
    } else {
      if (min < 60) return `${min}m ago`;
      if (hour < 24) return `${hour}h ago`;
      if (day < 7) return `${day}d ago`;
    }
    return d.toLocaleDateString();
  };
  useEffect(() => {
    fetchData();
  }, []);

  // Check if navigated from Venues page with a pre-selected venue
  useEffect(() => {
    if (location.state?.createGameForVenue) {
      setPreSelectedVenue(location.state.createGameForVenue);
      // Clear the navigation state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Open modal when venues are loaded and we have a pre-selected venue
  useEffect(() => {
    if (preSelectedVenue && venues.length > 0 && !loading) {
      handleOpenModalWithVenue(preSelectedVenue);
      setPreSelectedVenue(null);
    }
  }, [preSelectedVenue, venues, loading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [gamesRes, venuesRes, dealersRes, promosRes, leaguesRes] = await Promise.all([
        gamesApi.getAll().catch(() => ({ data: [], games: [] })),
        venuesApi.getAll().catch(() => ({ data: [], venues: [] })),
        usersApi.getDealers().catch(() => ({ data: [], dealers: [] })),
        promotionsApi.getAll().catch(() => ({ data: { promotions: [] } })),
        leaguesApi.getAll().catch(() => ({ data: { leagues: [] } })),
      ]);

      // Handle nested response structures
      const gamesList = gamesRes.data?.games || gamesRes.games || gamesRes.data || [];
      const venuesList = venuesRes.data?.venues || venuesRes.venues || venuesRes.data || [];
      const dealersList = dealersRes.data?.dealers || dealersRes.dealers || dealersRes.data || [];
      const promosList = promosRes.data?.promotions || promosRes.promotions || [];
      const leaguesList = leaguesRes?.data?.leagues || leaguesRes?.leagues || [];

      setGames(Array.isArray(gamesList) ? gamesList : []);
      setVenues(Array.isArray(venuesList) ? venuesList : []);
      setDealers(Array.isArray(dealersList) ? dealersList : []);
      setPromotions(Array.isArray(promosList) ? promosList : []);
      setAllLeagues(Array.isArray(leaguesList) ? leaguesList : []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    // Creating new game - Set default date/time to tomorrow at 7pm
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];

    setFormData({
      gameType: 'Texas Holdem',
      venueId: venues[0]?.id || '',
      startDate: dateStr,
      startTime: '19:00',
      maxPlaces: 10,
      promotionId: '',
    });
    setShowModal(true);
  };

  const handleOpenModalWithVenue = (venue) => {
    // Set default date/time to tomorrow at 7pm with pre-selected venue
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];

    setFormData({
      gameType: 'Texas Holdem',
      venueId: venue.id || '',
      startDate: dateStr,
      startTime: '19:00',
      maxPlaces: 10,
      promotionId: '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const startDateTime = new Date(`${formData.startDate}T${formData.startTime}:00`);

      const gameData = {
        gameType: formData.gameType,
        venueId: formData.venueId,
        startTime: startDateTime.toISOString(),
        maxPlaces: parseInt(formData.maxPlaces),
        isBigMoney: !!formData.isBigMoney,
      };

      const createRes = await gamesApi.create(gameData);
      const newGameId = createRes.data?.game?.id || createRes.data?.id || createRes.id;

      // Link to promotion if selected
      if (formData.promotionId && newGameId) {
        try {
          await promotionsApi.addGames(formData.promotionId, [newGameId]);
        } catch (e) {
          console.warn('Failed to link game to promotion:', e);
        }
      }

      handleCloseModal();
      fetchData();
    } catch (err) {
      console.error('Error saving game:', err);
      setError('Failed to create game');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game? This will also delete all associated data (tables, seats, waiting list).')) {
      return;
    }

    try {
      await gamesApi.delete(gameId);
      fetchData();
    } catch (err) {
      console.error('Error deleting game:', err);

      // Provide more specific error message
      let errorMessage = 'Failed to delete game';

      if (err.message?.toLowerCase().includes('500') || err.message?.toLowerCase().includes('internal server')) {
        errorMessage = '⚠️ Cannot delete game: The game has active players, seats, or other data that must be removed first. ' +
                      'Please contact your administrator or wait until the game is completed.';
      } else if (err.message?.includes('404')) {
        errorMessage = 'Game not found. It may have already been deleted.';
      } else if (err.message?.includes('403')) {
        errorMessage = 'Permission denied. You may not have authorization to delete this game.';
      } else if (err.message?.toLowerCase().includes('foreign key') || err.message?.toLowerCase().includes('constraint')) {
        errorMessage = '⚠️ Cannot delete game: This game has related data (players, tables, or waiting list entries) that must be removed first.';
      } else if (err.message) {
        errorMessage = `Unable to delete game: ${err.message.replace('Internal server error:', '').trim()}`;
      }

      setError(errorMessage);

      // Auto-clear error after 8 seconds (longer for more complex messages)
      setTimeout(() => setError(null), 8000);
    }
  };

  const handleOpenDealerModal = (game) => {
    setSelectedGame(game);
    setSelectedDealer(game.dealerId || '');
    setShowDealerModal(true);
  };

  const handleCloseDealerModal = () => {
    setShowDealerModal(false);
    setSelectedGame(null);
    setSelectedDealer('');
  };

  const handleAssignDealer = async () => {
    if (!selectedDealer || !selectedGame) return;

    try {
      setSaving(true);
      await gamesApi.assignDealer(selectedGame.id, selectedDealer);
      handleCloseDealerModal();
      fetchData();
    } catch (err) {
      console.error('Error assigning dealer:', err);
      setError('Failed to assign dealer');
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (dateStr, timeZone) => {
    const date = new Date(dateStr);
    const options = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    if (timeZone) options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };

  const getGameStatus = (game) => {
    const now = new Date();
    const startTime = new Date(game.startTime || game.start_time);

    if (startTime > now) {
      return { label: 'Upcoming', className: 'badge-info' };
    } else if (game.status === 'in_progress') {
      return { label: 'In Progress', className: 'badge-warning' };
    } else {
      return { label: 'Completed', className: 'badge-success' };
    }
  };

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="main-content">
        <div className="page-header">
          <h2>Games</h2>
          <p>Manage poker games, assign dealers, and view results</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Quick Stats Bar */}
        {!loading && games.length > 0 && (() => {
          const now = new Date();
          const todayStr = now.toDateString();
          const weekEnd = new Date(now); weekEnd.setDate(weekEnd.getDate() + 7);
          const stats = {
            total: games.length,
            today: games.filter(g => new Date(g.startTime || g.start_time).toDateString() === todayStr).length,
            thisWeek: games.filter(g => { const d = new Date(g.startTime || g.start_time); return d >= now && d <= weekEnd; }).length,
            missingDealer: games.filter(g => !(g.dealers && g.dealers.length > 0) && !g.dealerName).length,
            completed: games.filter(g => g.status === 'completed').length,
          };
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
              <StatTile label="Total" value={stats.total} color="#666" />
              <StatTile label="Today" value={stats.today} color="#2563eb" />
              <StatTile label="This Week" value={stats.thisWeek} color="#10b981" />
              <StatTile label="Missing Dealer" value={stats.missingDealer} color={stats.missingDealer > 0 ? "#dc2626" : "#10b981"} />
              <StatTile label="Completed" value={stats.completed} color="#888" />
            </div>
          );
        })()}

        <div className="card">
          <div className="card-header">
            <h3>All Games ({games.length})</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {selectedGameIds.size > 0 && (
                <>
                  <span style={{ alignSelf: 'center', fontSize: 13, color: '#666' }}>{selectedGameIds.size} selected</span>
                  <button className="btn btn-danger btn-sm" onClick={async () => {
                    if (!window.confirm(`Delete ${selectedGameIds.size} games? This cannot be undone.`)) return;
                    for (const id of selectedGameIds) { try { await gamesApi.delete(id); } catch {} }
                    setSelectedGameIds(new Set());
                    fetchData();
                  }}>Delete Selected</button>
                  <select className="form-select" style={{ width: 'auto', fontSize: 13 }} value="" onChange={async (e) => {
                    const promoId = e.target.value; if (!promoId) return;
                    await promotionsApi.addGames(promoId, Array.from(selectedGameIds));
                    setSelectedGameIds(new Set());
                    alert('Games added to promotion!');
                  }}>
                    <option value="">Add to Promotion...</option>
                    {promotions.filter(p => p.isActive).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button className="btn btn-secondary btn-sm" onClick={() => setSelectedGameIds(new Set())}>Deselect</button>
                </>
              )}
              <button className="btn btn-secondary" onClick={() => {
                // Apply same filters for export
                const searchLower = search.toLowerCase();
                const filtered = games.filter(g => {
                  const venueName = (g.venue?.name || g.venueName || '').toLowerCase();
                  const dealerStr = (g.dealers || []).map(d => d.name).join(' ').toLowerCase() + ' ' + (g.dealerName || '').toLowerCase();
                  const leagueName = (allLeagues.find(l => l.id === (g.leagueId || g.league_id))?.name || '').toLowerCase();
                  if (search && !venueName.includes(searchLower) && !dealerStr.includes(searchLower) && !leagueName.includes(searchLower)) return false;
                  if (filterLeague && (g.leagueId || g.league_id) !== filterLeague) return false;
                  if (filterVenue && (g.venue?.id || g.venueId || g.venue_id) !== filterVenue) return false;
                  return true;
                });
                exportCSV(
                  'games-export.csv',
                  ['Venue', 'Game Type', 'Start Time', 'Players', 'Dealers', 'Status'],
                  filtered.map(g => [
                    g.venue?.name || '',
                    g.gameType || g.game_type,
                    g.startTime || g.start_time,
                    g.occupiedSeats || 0,
                    (g.dealers || []).map(d => d.name).join('; ') || g.dealerName || '',
                    getGameStatus(g).label,
                  ])
                );
              }}>Export CSV</button>
              <button className="btn btn-primary" onClick={handleOpenModal}>+ Create Game</button>
            </div>
          </div>

          {/* Search + Filters Bar */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="🔍 Search venue, dealer, league..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="form-input"
                style={{ flex: 1, minWidth: 200 }}
              />
              <button className="btn btn-secondary btn-sm" onClick={() => setShowFilters(!showFilters)}>
                {showFilters ? '✕ Hide' : '⚙ Filters'}
              </button>
              <select className="form-select" style={{ width: 'auto' }} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                <option value="">No grouping</option>
                <option value="day">Group by Day</option>
                <option value="venue">Group by Venue</option>
                <option value="league">Group by League</option>
              </select>
              {(search || filterLeague || filterVenue || filterStatus || filterDealer || filterDateFrom || filterDateTo) && (
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  setSearch(''); setFilterLeague(''); setFilterVenue(''); setFilterStatus('');
                  setFilterDealer(''); setFilterDateFrom(''); setFilterDateTo(''); setPage(0);
                }}>Clear All</button>
              )}
            </div>
            {showFilters && (
              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                <select className="form-select" value={filterLeague} onChange={(e) => { setFilterLeague(e.target.value); setPage(0); }}>
                  <option value="">All leagues</option>
                  {allLeagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <select className="form-select" value={filterVenue} onChange={(e) => { setFilterVenue(e.target.value); setPage(0); }}>
                  <option value="">All venues</option>
                  {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <select className="form-select" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
                  <option value="">All statuses</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="missing">Missing Dealer</option>
                  <option value="assigned">Has Dealer</option>
                </select>
                <input type="date" className="form-input" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setPage(0); }} placeholder="From" />
                <input type="date" className="form-input" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setPage(0); }} placeholder="To" />
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
            </div>
          ) : games.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🎴</div>
              <h3>No games yet</h3>
              <p>Create your first poker game to get started.</p>
              <button className="btn btn-primary" onClick={handleOpenModal}>
                Create Game
              </button>
            </div>
          ) : (() => {
            // Filter
            const searchLower = search.toLowerCase();
            let filtered = games.filter(g => {
              const venueName = (g.venue?.name || g.venueName || '').toLowerCase();
              const dealerStr = (g.dealers || []).map(d => d.name).join(' ').toLowerCase() + ' ' + (g.dealerName || '').toLowerCase();
              const leagueName = (allLeagues.find(l => l.id === (g.leagueId || g.league_id))?.name || '').toLowerCase();
              if (search && !venueName.includes(searchLower) && !dealerStr.includes(searchLower) && !leagueName.includes(searchLower)) return false;
              if (filterLeague && (g.leagueId || g.league_id) !== filterLeague) return false;
              if (filterVenue && (g.venue?.id || g.venueId || g.venue_id) !== filterVenue) return false;
              if (filterDealer && !dealerStr.includes(filterDealer.toLowerCase())) return false;
              if (filterStatus) {
                const isCompleted = g.status === 'completed';
                const isUpcoming = new Date(g.startTime || g.start_time) > new Date();
                const hasDealer = (g.dealers && g.dealers.length > 0) || g.dealerName;
                if (filterStatus === 'completed' && !isCompleted) return false;
                if (filterStatus === 'upcoming' && !isUpcoming) return false;
                if (filterStatus === 'missing' && hasDealer) return false;
                if (filterStatus === 'assigned' && !hasDealer) return false;
              }
              if (filterDateFrom && new Date(g.startTime || g.start_time) < new Date(filterDateFrom)) return false;
              if (filterDateTo && new Date(g.startTime || g.start_time) > new Date(filterDateTo + 'T23:59:59')) return false;
              return true;
            });

            // Sort
            const sortFn = (a, b) => {
              let av, bv;
              switch (sortKey) {
                case 'venue': av = a.venue?.name || ''; bv = b.venue?.name || ''; break;
                case 'gameType': av = a.gameType || a.game_type || ''; bv = b.gameType || b.game_type || ''; break;
                case 'players': av = a.occupiedSeats || 0; bv = b.occupiedSeats || 0; break;
                case 'dealer': av = (a.dealers || []).map(d => d.name).join(',') || a.dealerName || ''; bv = (b.dealers || []).map(d => d.name).join(',') || b.dealerName || ''; break;
                case 'status': av = a.status || ''; bv = b.status || ''; break;
                case 'startTime':
                default:
                  av = new Date(a.startTime || a.start_time).getTime();
                  bv = new Date(b.startTime || b.start_time).getTime();
              }
              if (av < bv) return sortDir === 'asc' ? -1 : 1;
              if (av > bv) return sortDir === 'asc' ? 1 : -1;
              return 0;
            };
            const sorted = [...filtered].sort(sortFn);

            // Paginate
            const totalPages = Math.ceil(sorted.length / pageSize);
            const pageRows = sorted.slice(page * pageSize, (page + 1) * pageSize);

            // Group
            const groupedRows = {};
            if (groupBy) {
              for (const game of pageRows) {
                let key;
                if (groupBy === 'day') key = new Date(game.startTime || game.start_time).toDateString();
                else if (groupBy === 'venue') key = game.venue?.name || '—';
                else if (groupBy === 'league') key = allLeagues.find(l => l.id === (game.leagueId || game.league_id))?.name || '—';
                if (!groupedRows[key]) groupedRows[key] = [];
                groupedRows[key].push(game);
              }
            }

            const renderRow = (game) => {
              const status = getGameStatus(game);
              const startTime = new Date(game.startTime || game.start_time);
              const isToday = startTime.toDateString() === new Date().toDateString();
              const hasDealer = (game.dealers && game.dealers.length > 0) || game.dealerName;
              const rowBg = isToday ? '#eff6ff' : status.label === 'Completed' ? '#f9fafb' : !hasDealer && startTime > new Date() ? '#fff7ed' : '';
              return (
                <tr key={game.id} style={{ background: rowBg }}>
                  <td><input type="checkbox" checked={selectedGameIds.has(game.id)} onChange={() => {
                    setSelectedGameIds(prev => { const n = new Set(prev); if (n.has(game.id)) n.delete(game.id); else n.add(game.id); return n; });
                  }} /></td>
                  <td>{game.venue?.name || game.venueName || '-'}</td>
                  <td><strong>{game.gameType || game.game_type}</strong></td>
                  <td>
                    <div>{formatDateTime(game.startTime || game.start_time, game.leagueTimezone)}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{formatRelative(game.startTime || game.start_time)}</div>
                  </td>
                  <td>{game.occupiedSeats || 0} players</td>
                  <td>
                    {game.dealers && game.dealers.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                        {game.dealers.map((d, i) => (
                          <span key={d.id || i} className="badge badge-success" style={{ fontSize: 12 }}>
                            {d.name}{d.tableNumber ? ` (T${d.tableNumber})` : ''}
                          </span>
                        ))}
                      </div>
                    ) : game.dealerName ? (
                      <span className="badge badge-success">{game.dealerName}</span>
                    ) : (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleOpenDealerModal(game)}>Assign</button>
                    )}
                  </td>
                  <td><span className={`badge ${status.className}`}>{status.label}</span></td>
                  <td>
                    <div className="action-buttons">
                      <Link to={`/games/${game.id}`} className="btn btn-secondary btn-sm">Manage</Link>
                      <button className="btn btn-secondary btn-sm" title="Duplicate next week" onClick={() => handleDuplicate(game)}>📋</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(game.id)}>×</button>
                    </div>
                  </td>
                </tr>
              );
            };

            const SortHeader = ({ k, label }) => (
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(k)}>
                {label}{sortIcon(k)}
              </th>
            );

            return (
              <>
                <div style={{ padding: '8px 16px', fontSize: 13, color: '#666', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  Showing {pageRows.length} of {sorted.length} games (filtered from {games.length})
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input type="checkbox" checked={pageRows.length > 0 && pageRows.every(g => selectedGameIds.has(g.id))}
                          onChange={() => {
                            const allSelected = pageRows.every(g => selectedGameIds.has(g.id));
                            const next = new Set(selectedGameIds);
                            if (allSelected) pageRows.forEach(g => next.delete(g.id));
                            else pageRows.forEach(g => next.add(g.id));
                            setSelectedGameIds(next);
                          }} />
                      </th>
                      <SortHeader k="venue" label="Venue" />
                      <SortHeader k="gameType" label="Game Type" />
                      <SortHeader k="startTime" label="Start Time" />
                      <SortHeader k="players" label="Players" />
                      <SortHeader k="dealer" label="Dealer" />
                      <SortHeader k="status" label="Status" />
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupBy ? Object.keys(groupedRows).map(groupKey => (
                      <React.Fragment key={groupKey}>
                        <tr style={{ background: '#f3f4f6', fontWeight: 700 }}>
                          <td colSpan="8" style={{ padding: '8px 16px' }}>{groupKey} ({groupedRows[groupKey].length})</td>
                        </tr>
                        {groupedRows[groupKey].map(renderRow)}
                      </React.Fragment>
                    )) : pageRows.map(renderRow)}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 13, color: '#666' }}>Page {page + 1} of {totalPages}</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(0)}>« First</button>
                      <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Prev</button>
                      <button className="btn btn-secondary btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next ›</button>
                      <button className="btn btn-secondary btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>Last »</button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Create Game Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Create Game</h3>
                <button className="modal-close" onClick={handleCloseModal}>&times;</button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="gameType">Game Type</label>
                    <select
                      id="gameType"
                      name="gameType"
                      className="form-select"
                      value={formData.gameType}
                      onChange={handleInputChange}
                    >
                      <option value="Texas Holdem">Texas Holdem</option>
                      <option value="Omaha">Omaha</option>
                      <option value="Seven Card Stud">Seven Card Stud</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="venueId">Venue *</label>
                    <select
                      id="venueId"
                      name="venueId"
                      className="form-select"
                      value={formData.venueId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a venue</option>
                      {venues.map(venue => (
                        <option key={venue.id} value={venue.id}>{venue.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="startDate">Date *</label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      className="form-input"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="startTime">Time *</label>
                    <input
                      type="time"
                      id="startTime"
                      name="startTime"
                      className="form-input"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {promotions.length > 0 && (
                    <div className="form-group">
                      <label htmlFor="promotionId">Promotion</label>
                      <select
                        id="promotionId"
                        name="promotionId"
                        className="form-select"
                        value={formData.promotionId}
                        onChange={handleInputChange}
                      >
                        <option value="">None</option>
                        {promotions.filter(p => p.isActive).map(promo => (
                          <option key={promo.id} value={promo.id}>{promo.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!formData.isBigMoney}
                        onChange={(e) => setFormData(prev => ({ ...prev, isBigMoney: e.target.checked }))}
                      />
                      <span>💰 Big Money Game (winner auto-gets Bally's championship seat)</span>
                    </label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Creating...' : 'Create Game'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Assign Dealer Modal */}
        {showDealerModal && (
          <div className="modal-overlay" onClick={handleCloseDealerModal}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Assign Dealer</h3>
                <button className="modal-close" onClick={handleCloseDealerModal}>&times;</button>
              </div>

              <div className="modal-body">
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  Select a dealer for this game at {selectedGame?.venue?.name || 'the venue'}.
                </p>

                <div className="form-group">
                  <label htmlFor="dealer">Dealer</label>
                  <select
                    id="dealer"
                    className="form-select"
                    value={selectedDealer}
                    onChange={(e) => setSelectedDealer(e.target.value)}
                  >
                    <option value="">Select a dealer</option>
                    {dealers.map(dealer => (
                      <option key={dealer.id} value={dealer.id}>
                        {dealer.firstName && dealer.lastName
                          ? `${dealer.firstName} ${dealer.lastName}`
                          : dealer.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseDealerModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssignDealer}
                  disabled={!selectedDealer || saving}
                >
                  {saving ? 'Assigning...' : 'Assign Dealer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Games;
